import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Checkbox, FormControl, FormControlLabel } from '@mui/material';
import Explanation from '@/components/exercise/explanation';
import RichContent from '@/components/shared/rich-content';
import { useQuestionMarking } from '@/hooks';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import type { Question } from '@/types';
import { isExercise } from '@/types';
import { isAnswerCorrect } from '@/utils/server/course';

export default function AnswersCheckbox({
  question,
  result,
  preview,
}: {
  question: Question;
  result?: boolean;
  preview?: boolean;
}) {
  const { selectedCourse, selectedChapter } = useCourses();
  const { showExplanations } = useViewStore();
  const { id, answers, marking, solution, type } = question;

  const { updateMarking } = useQuestionMarking(selectedCourse?.id || '');

  const answerClass = (answerIndex: number): string => {
    if (!solution) return '';
    const isCorrect = solution.indexOf(answerIndex.toString()) > -1;
    return `${result ? 'result' : ''} ${isCorrect ? 'correct' : 'incorrect'}`;
  };

  const handleChange = (markingIndex: string): void => {
    if (!selectedChapter || !isExercise(selectedChapter)) return;
    updateMarking({
      question,
      update: markingIndex,
      exercise: selectedChapter,
    });
  };

  const explanationIsActive = showExplanations.indexOf(id) > -1;

  return (
    <>
      {answers?.map(({ id, answerNode, explanationNode, index }) => (
        <FormControl
          key={id}
          disabled={result || preview}
          sx={{
            '&.result.correct .MuiFormControlLabel-label': {
              color: '#16C36C',
            },
            '&.result.incorrect .MuiFormControlLabel-label': {
              color: '#FC5055',
            },
            '& .MuiFormControlLabel-label': { userSelect: 'none' },
            '& .MuiFormControlLabel-root': {
              alignItems: 'flex-start',
              '& .MuiCheckbox-root': { paddingTop: 0 },
            },
          }}
          className={answerClass(index)}>
          <FormControlLabel
            control={
              <Checkbox
                checked={marking.indexOf(index.toString()) > -1}
                onChange={() => handleChange(index.toString())}
                value={index.toString()}
              />
            }
            label={
              <Box
                sx={{
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}>
                <RichContent content={answerNode} noParagraphMargin />
                {result &&
                  (isAnswerCorrect(type, index, solution || []) ? (
                    <CheckCircle sx={{ color: '#16C36C' }} fontSize="small" />
                  ) : (
                    <Cancel sx={{ color: '#FC5055' }} fontSize="small" />
                  ))}
              </Box>
            }
          />
          {explanationIsActive && explanationNode && (
            <Box sx={{ mt: 1 }}>
              <Explanation
                explanationNode={explanationNode}
                isCorrect={isAnswerCorrect(type, index, solution || [])}
              />
            </Box>
          )}
        </FormControl>
      ))}
    </>
  );
}
